import { type ComponentProps, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Toaster as Sonner } from 'sonner'

import { usePortalContainer } from '@/hooks/use-portal-container'

// Hôte DOM des toasts, déplacé dans l'élément en plein écran quand il y en a un (cf. usePortalContainer) :
// sinon les toasts s'afficheraient invisibles derrière lui. On DÉPLACE le nœud plutôt que de changer la
// cible du portal : un nouveau conteneur remonterait le Toaster, dont l'état local perdrait les toasts en
// cours (sonner n'a pas d'option de conteneur).
function useToasterHost() {
  const container = usePortalContainer()
  const [host] = useState(() => document.createElement('div'))

  useLayoutEffect(() => {
    const parent = container ?? document.body
    parent.append(host)
  }, [container, host])

  useLayoutEffect(() => () => host.remove(), [host])

  return host
}

function Toaster({ ...props }: ComponentProps<typeof Sonner>) {
  const host = useToasterHost()

  return createPortal(
    <Sonner
      theme="light"
      expand
      className="toaster group"
      toastOptions={{
        classNames: {
          // Habillage réservé aux toasts stylés par sonner (data-styled) : un toast personnalisé (toast.custom, ex. le
          // toast de forçage) porte sa propre carte, qu'un fond et une ombre carrés posés sur le <li> déborderaient.
          toast:
            'group toast group-[.toaster]:data-[styled=true]:bg-background group-[.toaster]:data-[styled=true]:text-foreground group-[.toaster]:data-[styled=true]:border-border group-[.toaster]:data-[styled=true]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          warning: '[&_[data-icon]]:text-orange-400',
          success: '[&_[data-icon]]:text-green-400',
          error: '[&_[data-icon]]:text-destructive',
        },
        closeButton: true,
      }}
      {...props}
    />,
    host,
  )
}

export { Toaster }
